import React, { useEffect, useRef, useState } from 'react';
import { Copy, Check, MessagesSquare } from 'lucide-react';
import { useSessionStore } from '../../store/sessionStore';
import { MessageBubble } from './MessageBubble';
import { DoctorInput } from './DoctorInput';

export const ConversationPanel: React.FC = () => {
  const { messages } = useSessionStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleCopy = () => {
    const text = messages
      .map(msg => `[${msg.sender === 'patient' ? 'Paciente' : 'Médico'}] ${msg.text}`)
      .join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col h-full gap-3">

      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(14,165,233,0.2), rgba(20,184,166,0.15))', border: '1px solid rgba(14,165,233,0.2)' }}
          >
            <MessagesSquare className="w-4 h-4" style={{ color: '#38bdf8' }} />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white leading-none">Conversa</h2>
            <p className="text-[10px] text-slate-600 mt-0.5">Paciente ↔ Médico</p>
          </div>
        </div>

        {messages.length > 0 && (
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
            style={{
              background: copied ? 'rgba(45,212,191,0.1)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${copied ? 'rgba(45,212,191,0.3)' : 'rgba(255,255,255,0.08)'}`,
              color: copied ? '#2dd4bf' : '#64748b',
            }}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copiado!' : 'Copiar'}
          </button>
        )}
      </div>

      {/* Messages area */}
      <div
        className="flex-1 min-h-0 overflow-y-auto rounded-2xl p-4"
        style={{
          background: 'rgba(8,15,30,0.6)',
          border: '1px solid rgba(255,255,255,0.05)',
          backdropFilter: 'blur(12px)',
        }}
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ background: 'rgba(14,165,233,0.06)', border: '1px solid rgba(14,165,233,0.1)' }}
            >
              <MessagesSquare className="w-7 h-7" style={{ color: 'rgba(56,189,248,0.4)' }} />
            </div>
            <div className="text-center">
              <p className="text-slate-500 text-sm font-medium">Nenhuma mensagem ainda</p>
              <p className="text-slate-700 text-xs mt-1">
                Inicie a câmera e mostre um sinal em Libras
              </p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Doctor input */}
      <div className="flex-shrink-0">
        <DoctorInput />
      </div>
    </div>
  );
};
